import React, { createContext, useState, useEffect, ReactNode } from 'react'
import { User, AuthSession } from '@/core/domain/entities/User'
import { IAuthRepository } from '@/core/domain/repositories/IAuthRepository'
import { container, TYPES } from '@/core/container'
import { isOk } from '@/core/shared/utils/Result'

export interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (token: string) => Promise<boolean>
  logout: () => Promise<void>
  checkAuth: () => Promise<boolean>
  refreshAuth: () => Promise<void>
  theme?: string
  setTheme?: (theme: string) => void
  language?: string
  setLanguage?: (language: string) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const authRepository = container.resolve<IAuthRepository>(
    TYPES.AuthRepository
  )

  const isAuthenticated = user?.isAuthenticated || false

  useEffect(() => {
    initializeAuth()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const initializeAuth = async () => {
    try {
      setIsLoading(true)
      const sessionResult = await authRepository.getCurrentSession()

      if (isOk(sessionResult) && sessionResult.value) {
        const session = sessionResult.value
        setUser(session.user)

        console.log('📋 Session loaded from storage:', {
          userId: session.user.id,
          userType: session.user.type,
        })
      }
    } catch (error) {
      console.error('Failed to initialize auth:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (token: string): Promise<boolean> => {
    try {
      setIsLoading(true)
      const result = await authRepository.checkAuth(token)

      if (isOk(result)) {
        const authenticatedUser = result.value
        setUser(authenticatedUser)

        // Create and store session
        const session: AuthSession = {
          user: authenticatedUser,
          accessToken: token,
          expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000), // 24 hours
        }

        await authRepository.storeSession(session)
        return true
      }

      return false
    } catch (error) {
      console.error('Login failed:', error)
      return false
    } finally {
      setIsLoading(false)
    }
  }

  const logout = async (): Promise<void> => {
    try {
      await authRepository.clearSession()
      setUser(null)
      console.log('🚪 User logged out successfully')
    } catch (error) {
      console.error('Logout failed:', error)
      setUser(null)
    }
  }

  const refreshAuth = async (): Promise<void> => {
    console.log('🔄 Refreshing authentication state...')
    await initializeAuth()
  }

  const checkAuth = async (): Promise<boolean> => {
    try {
      const sessionResult = await authRepository.getCurrentSession()

      if (isOk(sessionResult) && sessionResult.value) {
        const session = sessionResult.value
        const checkResult = await authRepository.checkAuth(session.accessToken)

        if (isOk(checkResult)) {
          setUser(checkResult.value)
          return true
        }
      }

      // Clear invalid session
      await authRepository.clearSession()
      setUser(null)
      return false
    } catch (error) {
      console.error('Auth check failed:', error)
      return false
    }
  }

  const contextValue: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    checkAuth,
    refreshAuth,
  }

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  )
}

export { AuthContext }
